import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import LongTermAssetsModal from "./LongTermAssetsModal";
import LongTermAssetsViewModal from "./LongTermAssetsViewModal";
import "../../Style/TableStyle.scss";
import "../../Style/__searchbar.scss";
import "../../Style/__button.scss";
import "./LongTermAssetsPage.scss";
import { deleteAlert, errorAlert, handleSuccess } from "../../Components/Alerts";
import { ApiLink } from "../../APILink/ApiLink";
import authHeader from "../../Auth/authHeader";
import { AiFillCar } from "react-icons/ai";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ReusableServerDataGrid from "../../Components/ReusableServerDataGrid";
import CrudPageHeader from "../../Components/CrudPageHeader";

export default function LongTermAssetsPage() {
  const headers = authHeader();
  const [data, setData] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [viewAsset, setViewAsset] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
  const [sortModel, setSortModel] = useState([]);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [quickFilter, setQuickFilter] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(ApiLink.API_URL + "Accounts/LongTermAssets", { headers });
      setData(response.data);
    } catch (error) {
      if (error?.response?.data?.errorMessage) {
        errorAlert(`Gabim në marrjen e të dhënave: ${error.response.data.errorMessage}`);
      } else if (error?.message) {
        errorAlert(`Gabim: ${error.message}`);
      } else {
        errorAlert("Gabim i panjohur në kërkesë.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setSelectedAsset(item);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedAsset(null);
  };

  const handleView = (item) => {
    setViewAsset(item);
    setShowViewModal(true);
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setViewAsset(null);
  };

  const handleDelete = async (deleteItemId) => {
    try {
      const response = await axios.delete(
        ApiLink.API_URL + "Accounts/assets" + `/${deleteItemId}`,
        { headers }
      );
      const result = response.data;
      if (result.success) {
        handleSuccess("Aseti është fshirë me sukses");
        fetchData();
      } else {
        deleteAlert("Aseti nuk mund të fshihet sepse është përdorur në transaksione");
      }
    } catch (error) {
      deleteAlert("Ka ndodhur një gabim gjatë fshirjes së asetit.");
    }
  };

  const assetsGridHeader = [
    {
      field: "id",
      headerName: "#",
      width: 60,
      headerClassName: "super-app-theme--header",
      sortable: false,
      filterable: false,
      groupable: false,
      aggregable: false,
    },
    {
      field: "AccountName",
      headerName: "Emri i Asetit",
      flex: 2.6,
      minWidth: 320,
      editable: false,
      headerClassName: "super-app-theme--header",
      groupable: false,
      aggregable: false,
    },
    {
      field: "LedgerNameEN",
      headerName: "Llogaria Kontabël",
      flex: 1.2,
      minWidth: 200,
      editable: false,
      headerClassName: "super-app-theme--header",
      groupable: false,
      aggregable: false,
    },
    {
      field: "IsActive",
      headerName: "Aktiv",
      width: 90,
      headerClassName: "super-app-theme--header",
      renderCell: (params) =>
        params.value ? <CheckCircleOutlineIcon sx={{ color: "var(--cc-kpi-positive-text)" }} /> : "",
      filterType: "select",
      filterOptions: [
        { value: "true", label: "Aktiv" },
        { value: "false", label: "Joaktiv" },
      ],
    },
  ];

  const rows = data.map((item, index) => ({
    id: index + 1,
    AccountName: item.AccountName,
    LedgerNameEN: item.LedgerNameEN,
    IsActive: Boolean(item.IsActive),
    deleteItemId: item.AccountID,
    rowInfo: item,
  }));

  const filteredAndSortedRows = useMemo(() => {
    const searchText = quickFilter.join(" ").trim().toLowerCase();
    const activeFilters = filterModel?.items || [];

    const filtered = rows.filter((row) => {
      if (searchText) {
        const searchable = [row.AccountName, row.LedgerNameEN, row.IsActive ? "aktiv" : "joaktiv"]
          .join(" ")
          .toLowerCase();
        if (!searchable.includes(searchText)) return false;
      }

      return activeFilters.every((filter) => {
        const rawValue = row[filter.field];
        const expected = String(filter.value ?? "").toLowerCase();
        if (!expected) return true;
        if (filter.operator === "equals") {
          return String(rawValue).toLowerCase() === expected;
        }
        return String(rawValue ?? "").toLowerCase().includes(expected);
      });
    });

    const currentSort = sortModel?.[0];
    if (!currentSort?.field || !currentSort?.sort) return filtered;

    return [...filtered].sort((left, right) => {
      const leftValue = left[currentSort.field];
      const rightValue = right[currentSort.field];
      const comparison = String(leftValue ?? "").localeCompare(
        String(rightValue ?? ""),
        "sq",
        { numeric: true, sensitivity: "base" },
      );
      return currentSort.sort === "desc" ? -comparison : comparison;
    });
  }, [rows, filterModel, quickFilter, sortModel]);

  const paginatedRows = useMemo(() => {
    const start = paginationModel.page * paginationModel.pageSize;
    return filteredAndSortedRows.slice(start, start + paginationModel.pageSize);
  }, [filteredAndSortedRows, paginationModel]);

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div className="Crud__Container LTA__page">
      <LongTermAssetsModal
        show={showEditModal}
        onHide={handleCloseEditModal}
        mode="edit"
        initialData={selectedAsset}
        fetchData={fetchData}
      />

      <LongTermAssetsViewModal
        show={showViewModal}
        onHide={handleCloseViewModal}
        assetData={viewAsset}
      />

      <CrudPageHeader
        icon={AiFillCar}
        title="Lista e Aseteve Afatgjata"
        description="Forma e Aseteve: Menaxhoni, shtoni, editoni dhe fshini Asete."
        tutorialUrl="https://www.youtube.com/@Accountree"
        tutorialLabel="Shiko Tutorialin"
        action={
          <LongTermAssetsModal
            fetchData={fetchData}
            type="text"
            className="button-68"
          />
        }
        className="LTA__header"
      />

      <ReusableServerDataGrid
        columns={assetsGridHeader}
        rows={paginatedRows}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onView={handleView}
        showViewAction={true}
        showPayment={false}
        actionsMinWidth={110}
        actionsFlex={0.8}
        height={500}
        rowCount={filteredAndSortedRows.length}
        loading={loading}
        paginationModel={paginationModel}
        setPaginationModel={setPaginationModel}
        sortModel={sortModel}
        setSortModel={setSortModel}
        filterModel={filterModel}
        setFilterModel={setFilterModel}
        quickFilter={quickFilter}
        setQuickFilter={setQuickFilter}
        getExportRows={() => Promise.resolve(filteredAndSortedRows)}
      />
    </div>
  );
}
